/** Small draft helpers shared by the ops. */
import { newId } from '../../shared/plan-schema';
import type { NodeId, PlanDoc, VertId } from '../../shared/types';
import { clamp } from '../geom/basic';

/** Smallest and largest road width a dot may have (plan schema limits). */
export const NODE_W_MIN = 0.5;
export const NODE_W_MAX = 100;
/** Smallest and largest river width (plan schema limits). */
export const RIVER_W_MIN = 0.5;
export const RIVER_W_MAX = 200;

/** A fresh id that isn't used yet in the given record. */
export function freshId(rec: Record<string, unknown>): string {
  let id = newId();
  while (Object.hasOwn(rec, id)) id = newId();
  return id;
}

/** Adds a new corner to the pool and returns its id. */
export function addVert(draft: PlanDoc, x: number, y: number, pinned?: boolean): VertId {
  const id = freshId(draft.verts);
  draft.verts[id] = pinned ? { x, y, pinned: true } : { x, y };
  return id;
}

/** Adds a new road dot and returns its id. */
export function addNode(draft: PlanDoc, x: number, y: number, w: number): NodeId {
  const id = freshId(draft.nodes);
  draft.nodes[id] = { x, y, w: clamp(w, NODE_W_MIN, NODE_W_MAX) };
  return id;
}

/** Clamps a position to the map square. */
export function clampToMap(doc: PlanDoc, x: number, y: number): [number, number] {
  return [clamp(x, 0, doc.size), clamp(y, 0, doc.size)];
}

/** Removes corners no ring uses and dots no road uses. Run after any op that drops references. */
export function pruneOrphans(draft: PlanDoc): void {
  const usedV = new Set<string>(draft.plot?.verts ?? []);
  for (const a of Object.values(draft.areas)) for (const v of a.verts) usedV.add(v);
  for (const id of Object.keys(draft.verts)) if (!usedV.has(id)) delete draft.verts[id];
  const usedN = new Set<string>();
  for (const r of Object.values(draft.roads)) for (const n of r.nodes) usedN.add(n);
  for (const id of Object.keys(draft.nodes)) if (!usedN.has(id)) delete draft.nodes[id];
}

/** Removes consecutive repeats from a ring or dot list (and the wrap-around repeat when closed). */
export function dedupeConsecutive<T>(ids: T[], closed: boolean): T[] {
  const out = ids.filter((id, i) => i === 0 || id !== ids[i - 1]);
  while (closed && out.length > 1 && out[0] === out[out.length - 1]) out.pop();
  return out;
}
